import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { format } from 'date-fns';
import { EmptyState, Icon } from '../../components';
import {
  fetchAllParks,
  fetchAllParksCustomer,
  getChangeHistory,
  getCustomers,
} from '../../services';
import { notifyError } from '../../utils';
import * as s from './styled-audit-logs';

const TYPE_LABEL = {
  setpoint: 'Setpoint',
  command: 'Comando',
  range: 'Range',
};

const TYPE_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'setpoint', label: 'Setpoints' },
  { value: 'command', label: 'Comandos' },
  { value: 'range', label: 'Ranges' },
];

const normalizeList = (data) => {
  if (Array.isArray(data?.[0])) return data[0];
  if (Array.isArray(data)) return data;
  return [];
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return format(date, 'dd/MM/yyyy HH:mm');
};

const stringifyValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';

  if (typeof value !== 'string') {
    return JSON.stringify(value);
  }

  const present = (item) => item !== null && item !== undefined && item !== '';

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed
        .map((item) => {
          const limits = [item.min, item.max].filter(present).join(' - ');
          return [item.category, item.level, limits, item.unit]
            .filter(present)
            .join(' ');
        })
        .filter(present)
        .join(' | ');
    }
    return JSON.stringify(parsed);
  } catch {
    return value;
  }
};

const AuditLogs = () => {
  const [items, setItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [type, setType] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [parkId, setParkId] = useState('');
  const [customers, setCustomers] = useState([]);
  const [parks, setParks] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const fetchTokenRef = useRef(0);
  const filterTokenRef = useRef(0);

  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

  const fetchLogs = useCallback(() => {
    fetchTokenRef.current += 1;
    const token = fetchTokenRef.current;
    setLoading(true);

    getChangeHistory(currentPage, itemsPerPage, {
      type,
      customerId,
      parkId,
    })
      .then(({ data }) => {
        if (token !== fetchTokenRef.current) return;
        setItems(data?.items || []);
        setTotalItems(data?.totalItems || 0);
      })
      .catch((error) => {
        if (token !== fetchTokenRef.current) return;
        setItems([]);
        setTotalItems(0);
        notifyError(
          error?.message || 'Ocorreu um erro ao carregar os logs de alteração'
        );
      })
      .finally(() => {
        if (token === fetchTokenRef.current) setLoading(false);
      });
  }, [currentPage, itemsPerPage, type, customerId, parkId]);

  const loadParks = useCallback((selectedCustomerId) => {
    filterTokenRef.current += 1;
    const token = filterTokenRef.current;
    setLoadingFilters(true);

    const request = selectedCustomerId
      ? fetchAllParksCustomer(selectedCustomerId)
      : fetchAllParks();

    request
      .then(({ data }) => {
        if (token !== filterTokenRef.current) return;
        setParks(normalizeList(data));
      })
      .catch(() => {
        if (token !== filterTokenRef.current) return;
        setParks([]);
        notifyError('Ocorreu um erro ao carregar os parques');
      })
      .finally(() => {
        if (token === filterTokenRef.current) setLoadingFilters(false);
      });
  }, []);

  useEffect(() => {
    getCustomers('', '', '', '', '')
      .then(({ data }) => setCustomers(normalizeList(data)))
      .catch(() => {
        setCustomers([]);
        notifyError('Ocorreu um erro ao carregar os clientes');
      });
  }, []);

  useEffect(() => {
    setParkId('');
    loadParks(customerId);
  }, [customerId, loadParks]);

  useEffect(() => {
    fetchLogs();
    return () => {
      fetchTokenRef.current += 1;
    };
  }, [fetchLogs]);

  useEffect(() => {
    setCurrentPage(1);
  }, [type, customerId, parkId, itemsPerPage]);

  const rows = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        typeLabel: TYPE_LABEL[item.type] || item.type,
        previousValueLabel: stringifyValue(item.previousValue),
        currentValueLabel: stringifyValue(item.currentValue),
        changedAtLabel: formatDateTime(item.changedAt),
      })),
    [items]
  );

  return (
    <s.Container>
      <s.PageHeader>
        <s.PageHeaderText>
          <s.PageTitle>Logs de alteração</s.PageTitle>
          <s.PageSub>
            Setpoints, comandos e ranges alterados nos últimos 30 dias
          </s.PageSub>
        </s.PageHeaderText>
        <s.PageCount>
          <b>{totalItems}</b> registro{totalItems === 1 ? '' : 's'}
        </s.PageCount>
      </s.PageHeader>

      <s.Filters>
        <s.FilterCell>
          <span className="k">Tipo</span>
          <select
            value={type}
            onChange={(event) => setType(event.target.value)}
          >
            {TYPE_OPTIONS.map((option) => (
              <option key={option.value || 'all'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </s.FilterCell>
        <s.FilterCell>
          <span className="k">Cliente</span>
          <select
            value={customerId}
            onChange={(event) => setCustomerId(event.target.value)}
          >
            <option value="">Todos</option>
            {customers.map((customer) => (
              <option key={customer.id} value={customer.id}>
                {customer.name}
              </option>
            ))}
          </select>
        </s.FilterCell>
        <s.FilterCell>
          <span className="k">Parque</span>
          <select
            value={parkId}
            onChange={(event) => setParkId(event.target.value)}
            disabled={loadingFilters}
          >
            <option value="">Todos</option>
            {parks.map((park) => (
              <option key={park.id} value={park.id}>
                {park.name}
              </option>
            ))}
          </select>
        </s.FilterCell>
        <s.ActionButton type="button" onClick={fetchLogs} disabled={loading}>
          <Icon name="refresh" size={16} />
          Atualizar
        </s.ActionButton>
      </s.Filters>

      <s.TableWrap>
        <s.Table>
          <s.Th>Tipo</s.Th>
          <s.Th $align="left">Equipamento</s.Th>
          <s.Th $align="left">Parque</s.Th>
          <s.Th $align="left">Item</s.Th>
          <s.Th>Anterior</s.Th>
          <s.Th>Atual</s.Th>
          <s.Th $align="left">Usuário</s.Th>
          <s.Th $align="left">Nome</s.Th>
          <s.Th>Data e hora</s.Th>

          {loading ? (
            Array.from({ length: itemsPerPage }, (_, index) => index).map(
              (index) => (
                <s.Tr key={`loading-${index}`}>
                  <s.SkeletonCell $w="60%" />
                  <s.SkeletonCell $w="70%" />
                  <s.SkeletonCell $w="70%" />
                  <s.SkeletonCell $w="75%" />
                  <s.SkeletonCell $w="80%" />
                  <s.SkeletonCell $w="80%" />
                  <s.SkeletonCell $w="70%" />
                  <s.SkeletonCell $w="70%" />
                  <s.SkeletonCell $w="75%" />
                </s.Tr>
              )
            )
          ) : rows.length ? (
            rows.map((row) => (
              <s.Tr key={`${row.type}-${row.id}`}>
                <s.Tc data-label="Tipo">
                  <s.TypeBadge $type={row.type}>{row.typeLabel}</s.TypeBadge>
                </s.Tc>
                <s.TextCell
                  data-label="Equipamento"
                  title={row.thingName || ''}
                >
                  {row.thingName || '—'}
                </s.TextCell>
                <s.TextCell data-label="Parque" title={row.parkName || ''}>
                  {row.parkName || '—'}
                </s.TextCell>
                <s.TextCell data-label="Item" title={row.itemName || ''}>
                  {row.itemName || '—'}
                </s.TextCell>
                <s.ValueCell
                  data-label="Valor anterior"
                  title={row.previousValueLabel}
                  $tone="previous"
                >
                  {row.previousValueLabel}
                </s.ValueCell>
                <s.ValueCell
                  data-label="Valor atual"
                  title={row.currentValueLabel}
                  $tone="current"
                >
                  {row.currentValueLabel}
                </s.ValueCell>
                <s.TextCell data-label="Usuário" title={row.userName || ''}>
                  {row.userName || '—'}
                </s.TextCell>
                <s.TextCell
                  data-label="Nome"
                  title={row.userDisplayName || ''}
                >
                  {row.userDisplayName || '—'}
                </s.TextCell>
                <s.DateCell data-label="Data e hora">
                  {row.changedAtLabel}
                </s.DateCell>
              </s.Tr>
            ))
          ) : (
            <s.EmptyRow>
              <EmptyState
                title="Nenhum log encontrado"
                description="Não há alterações registradas para o filtro selecionado."
                actionLabel="Atualizar"
                onAction={fetchLogs}
              />
            </s.EmptyRow>
          )}
        </s.Table>
      </s.TableWrap>

      <s.Pager>
        <button
          type="button"
          className="btn"
          onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
          disabled={loading || currentPage <= 1}
        >
          Anterior
        </button>
        <span className="page">
          {currentPage} / {totalPages}
        </span>
        <button
          type="button"
          className="btn"
          onClick={() =>
            setCurrentPage((page) => Math.min(totalPages, page + 1))
          }
          disabled={loading || currentPage >= totalPages}
        >
          Próximo
        </button>
        <div className="exhibit">
          exibir
          <select
            value={itemsPerPage}
            onChange={(event) => setItemsPerPage(Number(event.target.value))}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
      </s.Pager>
    </s.Container>
  );
};

export default AuditLogs;
